import React, { useContext, useState, useEffect } from 'react';

import { makeStyles } from '@mui/styles';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  TablePagination,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PatientTabsContext } from '../../../lib/contexts';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { PatientRecommendedChallengesRow } from './index';
import usePatientRecommendedChallenges from '../../../lib/hooks/usePatientRecommendedChallenges';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    marginTop: 16,
  },
  container: theme.globalClasses.adminTableContainer,
  topBar: {
    marginBottom: 16,
  },
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
}));

const PatientRecommendedChallengesBox = ({
  summary,
  setSetSubtab,
  challenges: externalChallenges,
  isLoading: externalIsLoading,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const matchLG = useMediaQuery(theme?.breakpoints?.up('lg'));
  const matchXL = useMediaQuery(theme?.breakpoints?.up('xl'));
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);

  // Track whether we should use props or the hook
  const useProvidedChallenges = externalChallenges !== undefined;

  // Call the hook with the current tab ID or undefined based on whether we have provided challenges
  // The hook has enabled: !!patientID so it won't fetch when patientID is undefined
  const {
    isLoading: hookIsLoading,
    data: hookData,
    refetch,
  } = usePatientRecommendedChallenges(useProvidedChallenges ? undefined : currentTab.id);

  // Determine which data source and loading state to use
  const challengesData = useProvidedChallenges ? externalChallenges : hookData || [];
  const isLoading = useProvidedChallenges ? externalIsLoading : hookIsLoading;

  // Memoize data to prevent unnecessary rerenders
  const challenges = React.useMemo(() => challengesData, [challengesData]);
  const hasData = React.useMemo(() => Boolean(challenges && challenges.length > 0), [challenges]);

  // Add state for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLastPage, setIsLastPage] = useState(false);

  // Update isLastPage when data changes
  useEffect(() => {
    if (hasData) {
      setIsLastPage((page + 1) * rowsPerPage >= challenges.length);
    }
  }, [challenges, page, rowsPerPage, hasData]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    if (!hasData) return;
    if (!isLastPage || newPage < page) {
      setPage(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    if (!hasData) return;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className="patient-recommended-challenges-box"
          id="patient-recommended-challenges-box"
        >
          <Grid
            item
            container
            xs={12}
            className={classes.topBar}
            justifyContent="space-between"
            sx={{ width: '100%', maxWidth: '100%', mx: 0, padding: 0 }}
          >
            <Grid item>
              <Typography variant={'h2'}>Challenge Log</Typography>
            </Grid>
          </Grid>

          {isLoading ? (
            <AdminProgressBar />
          ) : hasData ? (
            <Box className={classes.container}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell
                      align="left"
                      sx={{
                        width: '20%', // Approximately 176px in a standard 880px table
                        maxWidth: '176px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Challenge Name
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: '47%', // Approximately 418px in a standard 880px table
                        maxWidth: '418px',
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: '18%', // Approximately 104px in a standard 880px table
                        maxWidth: '150px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Success Rate
                        <Tooltip
                          title="Percent of check-ins recorded out of all possible check ins per challenges."
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ ml: 0.5, cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: '15%', // Approximately 104px in a standard 880px table
                        maxWidth: '150px',
                      }}
                    >
                      Total Positive Days
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {challenges
                    .slice(summary ? 0 : page * rowsPerPage, summary ? 3 : page * rowsPerPage + rowsPerPage)
                    .map((item, index) => {
                      return (
                        <PatientRecommendedChallengesRow
                          key={index}
                          row={item}
                          summary={summary}
                          refetch={refetch}
                        />
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          ) : null}

          {!summary && hasData && !isLoading && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={challenges.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              getItemAriaLabel={(type) => `Go to ${type} page`}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientRecommendedChallengesBox;
